/** Generates a random 6-digit hex color, e.g. "#a3c1f2". Used to seed new
 * label colors before the user picks one explicitly. */
export const getRandomHex = () => {
  const hex = Math.floor(Math.random() * 16777215).toString(16);
  return `#${hex.padStart(6, "0")}`;
};

/** Splits "#rrggbb" into its channels; null when the color is not a plain hex
 * triple (it is unvalidated at the API level). */
const parseHex = (hex: string): [number, number, number] | null => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** WCAG relative luminance of an sRGB triple. */
const relativeLuminance = (r: number, g: number, b: number) => {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

/** Flattens `alpha` of `c` over the opaque channel `under`. */
const over = (c: number, alpha: number, under: number) =>
  Math.round(under + alpha * (c - under));

/** Picks black or white text for a given background color so user-chosen
 * label colors stay readable. */
export function getContrastTextColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return "var(--foreground)";
  return relativeLuminance(...rgb) > 0.4 ? "#000000" : "#ffffff";
}

/**
 * Darkens a label color until text painted in it clears 4.9:1 against a keycap
 * tinted with that same color — worst case a 16% tint sitting on the active
 * chip's own 18% tint over white. Label colors are user-picked and often far
 * too light to read at 11px; stepping the channels down 10% at a time keeps the
 * hue recognisable while buying the contrast.
 */
export function getReadableLabelColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const cap = rgb.map((c) => over(c, 0.16, over(c, 0.18, 255))) as [
    number,
    number,
    number,
  ];
  const capLuminance = relativeLuminance(...cap);
  let [r, g, b] = rgb;
  const contrast = () =>
    (capLuminance + 0.05) / (relativeLuminance(r, g, b) + 0.05);
  for (let guard = 0; contrast() < 4.9 && guard < 40; guard++) {
    if (r + g + b === 0) break;
    r = Math.round(r * 0.9);
    g = Math.round(g * 0.9);
    b = Math.round(b * 0.9);
  }
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}
